import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import api from "../../services/api";
import { APP_VERSION } from "../../version";

// Polls the backend for its expected app version. If the value diverges
// from APP_VERSION (the constant baked into this bundle at build time),
// renders a blocking overlay with a Reload button. This is the safety net
// for IIS / browser cache: when a new build ships, users on a stale bundle
// are prompted to reload instead of silently running outdated code.
//
// Default poll interval: 60s. Initial check: on mount.

const POLL_MS = 60_000;

const hardReload = () => {
  try {
    if (typeof window !== "undefined" && "caches" in window) {
      // Best-effort: clear service-worker caches (no-op if none).
      window.caches
        .keys()
        .then((names) => Promise.all(names.map((n) => window.caches.delete(n))))
        .catch(() => {})
        .finally(() => window.location.reload());
      return;
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
};

export default function VersionGate({ children }) {
  const [serverVersion, setServerVersion] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const check = async () => {
      try {
        const { data } = await api.get("/version");
        const raw = data?.data?.version;
        // accept either a number or a stringified number from the server
        const v = raw == null ? null : Number(raw);
        if (alive && Number.isFinite(v)) setServerVersion(v);
      } catch {
        // network errors should not trigger a false "mismatch"
      }
    };

    check();
    timerRef.current = setInterval(check, POLL_MS);
    return () => {
      alive = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const mismatch =
    Number.isFinite(serverVersion) && serverVersion !== APP_VERSION;

  return (
    <>
      {children}
      {mismatch && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (t) => t.zIndex.modal + 100,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 420,
              width: "100%",
              p: 3,
              borderRadius: "14px",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: (t) => t.customShadows?.lg,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "primary.light",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 1.5,
              }}
            >
              <RefreshIcon fontSize="large" />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              New version available
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              The application has been updated. Reload now to load the latest
              version and avoid using stale data.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mb: 1.5 }}
            >
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.6875rem",
                  fontFamily: "monospace",
                  borderRadius: "4px",
                  backgroundColor: "action.hover",
                  color: "text.secondary",
                }}
              >
                Loaded: {APP_VERSION}
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  fontSize: "0.6875rem",
                  fontFamily: "monospace",
                  borderRadius: "4px",
                  backgroundColor: "primary.light",
                  color: "primary.main",
                  fontWeight: 600,
                }}
              >
                Available: {serverVersion}
              </Box>
            </Stack>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<RefreshIcon />}
              onClick={hardReload}
              sx={{
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #0d9488 0%, #0e7490 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #0f766e 0%, #155e75 100%)",
                },
              }}
            >
              Reload now
            </Button>
          </Paper>
        </Box>
      )}
    </>
  );
}
